/**
 * Shared SVG icons for link types
 * Centralized to avoid duplication across components
 */

import type { ReactElement } from 'react';

export type LinkIconType = 'github' | 'demo' | 'external' | 'figma';

interface LinkIconProps {
  className?: string;
}

const GitHubIcon = ({ className = 'w-5 h-5' }: LinkIconProps) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
      clipRule="evenodd"
    />
  </svg>
);

const FigmaIcon = ({ className = 'w-5 h-5' }: LinkIconProps) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 12a4 4 0 1 1 8 0 4 4 0 0 1-8 0zm-4-4a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 0a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 12a4 4 0 1 0 0-8v8zm4-12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
  </svg>
);

const DemoIcon = ({ className = 'w-5 h-5' }: LinkIconProps) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

const ExternalIcon = ({ className = 'w-5 h-5' }: LinkIconProps) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

/**
 * Map of link type to icon component
 * Use this to get the appropriate icon for a link type
 */
export const linkIcons: Record<LinkIconType, (props: LinkIconProps) => ReactElement> = {
  github: GitHubIcon,
  figma: FigmaIcon,
  demo: DemoIcon,
  external: ExternalIcon,
};

/**
 * Get an icon component for a given link type
 */
export const getLinkIcon = (type: LinkIconType, className?: string) => {
  const IconComponent = linkIcons[type];
  return <IconComponent className={className} />;
};
